import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Business,
  Person,
  Security,
} from '@mui/icons-material';
import { useInviteDetails, useAcceptInvite } from '../hooks/useUsers';

const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: invite, isLoading, error } = useInviteDetails(token || '');
  const acceptMutation = useAcceptInvite();

  // Password validation
  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /\d/.test(formData.password),
    match: formData.password === formData.confirmPassword && formData.password.length > 0,
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!isPasswordValid) {
      setSubmitError('Please ensure your password meets all requirements');
      return;
    }

    try {
      await acceptMutation.mutateAsync({
        token: token || '',
        data: {
          password: formData.password,
          name: formData.name || undefined,
        },
      });
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Accept invite error:', err);
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to accept invite');
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#121212',
        }}
      >
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (error || !invite) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#121212',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center', bgcolor: '#1e1e1e' }}>
          <Cancel sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Invalid Invite
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This invite link is invalid, has expired, or has already been used.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  if (invite.is_expired) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#121212',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center', bgcolor: '#1e1e1e' }}>
          <Cancel sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Invite Expired
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This invite has expired. Please contact your administrator to request a new one.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#121212',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center', bgcolor: '#1e1e1e' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Account Activated!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Your account has been created successfully. Redirecting to login...
          </Typography>
          <CircularProgress size={24} sx={{ color: '#6366f1' }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#121212',
        p: 3,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 480, width: '100%', bgcolor: '#1e1e1e' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#6366f1', mb: 1 }}>
            JustPlay
          </Typography>
          <Typography variant="h5" gutterBottom>
            Welcome!
          </Typography>
          <Typography color="text.secondary">
            You've been invited to join <strong>{invite.organization_name}</strong>
          </Typography>
        </Box>

        {/* Invite Details */}
        <Box sx={{ bgcolor: '#252525', borderRadius: 2, p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Business sx={{ color: '#6366f1', fontSize: 20 }} />
            <Typography variant="body2">
              Organization: <strong>{invite.organization_name}</strong>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Person sx={{ color: '#6366f1', fontSize: 20 }} />
            <Typography variant="body2">
              Role: <strong>{invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}</strong>
            </Typography>
          </Box>
          {invite.inviter_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ color: '#6366f1', fontSize: 20 }} />
              <Typography variant="body2">
                Invited by: <strong>{invite.inviter_name}</strong>
              </Typography>
            </Box>
          )}
        </Box>

        {invite.message && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              "{invite.message}"
            </Typography>
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              value={invite.email}
              disabled
              fullWidth
            />

            <TextField
              label="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              placeholder={invite.name || 'Enter your full name'}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              fullWidth
              required
              error={formData.confirmPassword.length > 0 && !passwordChecks.match}
              helperText={formData.confirmPassword.length > 0 && !passwordChecks.match ? 'Passwords do not match' : ''}
            />

            {/* Password Requirements */}
            <Box sx={{ bgcolor: '#252525', borderRadius: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Security sx={{ color: '#6366f1', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={500}>Password Requirements</Typography>
              </Box>
              <List dense disablePadding>
                {[
                  { key: 'length', label: 'At least 8 characters' },
                  { key: 'uppercase', label: 'One uppercase letter' },
                  { key: 'lowercase', label: 'One lowercase letter' },
                  { key: 'number', label: 'One number' },
                  { key: 'match', label: 'Passwords match' },
                ].map(({ key, label }) => (
                  <ListItem key={key} disablePadding sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {passwordChecks[key as keyof typeof passwordChecks] ? (
                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                      ) : (
                        <Cancel sx={{ fontSize: 18, color: 'text.disabled' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: passwordChecks[key as keyof typeof passwordChecks] ? 'success.main' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!isPasswordValid || acceptMutation.isPending}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, mt: 1 }}
            >
              {acceptMutation.isPending ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Create Account'
              )}
            </Button>
          </Box>
        </form>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AcceptInvitePage;
